// src/pages/companies/CompanyList.tsx
import { Column, DataTable } from '@/components/common/DataTable';
import DeleteConfirmDialog from '@/components/common/DeleteConfirmDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ASSET_BASE_URL } from '@/config';
import useDebounce from '@/hooks/use-debounce';
import { DEFAULT_PAGINATION, getCurrencyLabel } from '@/lib/constants/common';
import { getIndustryLabel } from '@/lib/constants/company';
import { companiesAPI } from '@/lib/utils/api';
import { Pagiantion } from '@/types/common.type';
import { Company } from '@/types/company.types';
import { Building, Edit, Files, Globe, Plus, Search, Trash2 } from 'lucide-react';
import { ChangeEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

const CompanyList = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 300);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem('companies_rowsPerPage');
    return saved ? Number(saved) : 10;
  });
  const [totalCompanies, setTotalCompanies] = useState<number>(0);

  const fetchCompanies = async (): Promise<void> => {
    try {
      setLoading(true);

      const params: any = {};
      if (debouncedSearch) params.search = debouncedSearch;
      params.page = currentPage;
      params.limit = rowsPerPage;

      const response = await companiesAPI.getAll(params);
      const {
        total,
        limit = rowsPerPage,
        page,
        pages,
      }: Pagiantion = response.data?.pagination ?? DEFAULT_PAGINATION;

      setCompanies(
        Array.isArray(response?.data) ? response?.data : (response.data.companies ?? [])
      );
      setTotalCompanies(total);
    } catch (error) {
      console.log('🚀 ~ fetchCompanies ~ error:', error);
      toast.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, rowsPerPage]);

  useEffect(() => {
    fetchCompanies();
  }, [currentPage, debouncedSearch, rowsPerPage]);

  const handleDuplicate = async (company: Company): Promise<void> => {
    try {
      const duplicatedData = {
        ...company,
        companyName: `${company.companyName} (Copy)`,
      };

      delete (duplicatedData as Partial<Company>)._id;

      await companiesAPI.create(duplicatedData);
      toast.success('Company duplicated successfully');
      fetchCompanies();
    } catch {
      toast.error('Failed to duplicate company');
    }
  };

  const handleDeleteClick = (company: Company): void => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!companyToDelete) return;

    try {
      setDeleting(true);
      await companiesAPI.delete(companyToDelete._id);
      toast.success('Company deleted successfully');
      fetchCompanies();
    } catch {
      toast.error('Failed to delete company');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
    }
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
    localStorage.setItem('companies_rowsPerPage', String(rows));
  };

  const columns: Column<Company>[] = [
    {
      key: 'company',
      header: 'Company',
      cell: (company) => (
        <div className="flex items-center gap-3">
          {company.companyLogo ? (
            <div className="h-12 w-12 rounded-lg border bg-background overflow-hidden shrink-0">
              <img
                src={`${ASSET_BASE_URL}${company.companyLogo}`}
                className="h-full w-full object-contain p-1"
                alt={company.companyName}
              />
            </div>
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-lg border bg-muted shrink-0">
              <Building className="h-6 w-6 text-muted-foreground" />
            </div>
          )}

          <div>
            <div className="font-medium whitespace-nowrap w-fit min-w-fit">
              {company.companyName}
            </div>
            <div className="text-sm text-muted-foreground whitespace-nowrap w-fit min-w-fit">
              {company.companyAddress?.city || 'No location'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'industry',
      header: 'Industry',
      cell: (company) =>
        company.companyIndustry ? (
          <Badge variant="outline">{getIndustryLabel(company.companyIndustry)}</Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'website',
      header: 'Website',
      cell: (company) =>
        company.companyWebsite ? (
          <a
            href={company.companyWebsite}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:underline"
          >
            <Globe className="h-3 w-3" />
            Visit
          </a>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'currency',
      header: 'Currency',
      cell: (company) => (
        <Badge variant="secondary" className="font-mono">
          {company.baseCurrency ? getCurrencyLabel(company.baseCurrency) : 'USD'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right sticky right-0',
      cell: (company) => (
        <div className="flex justify-end gap-2">
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="text-blue-600 hover:bg-blue-50 hover:text-blue-600"
          >
            <Link to={`/companies/${company._id}/edit`}>
              <Edit className="!h-[18px] !w-[18px]" />
            </Link>
          </Button>
          <Button variant="ghost" size="icon" onClick={() => handleDuplicate(company)}>
            <Files className="!h-[18px] !w-[18px]" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-red-600 hover:bg-red-50 hover:text-red-600"
            onClick={() => handleDeleteClick(company)}
          >
            <Trash2 className="!h-[18px] !w-[18px]" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-muted-foreground">Manage your company profiles and information</p>
        </div>
        <Button asChild>
          <Link to="/companies/new">
            <Plus className="mr-2 h-4 w-4" />
            Add Company
          </Link>
        </Button>
      </div>

      {/* Search Card */}
      <Card>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search companies by name, industry, city, or state"
              value={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              className="pl-10 max-w-96"
            />
          </div>
        </CardContent>
      </Card>

      {/* Companies List */}
      <Card>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4 rounded-lg border p-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : companies.length === 0 ? (
            <div className="py-12 text-center">
              <Building className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">No companies found</h3>
              <p className="mb-4 text-muted-foreground">
                {search
                  ? 'Try a different search term'
                  : 'Get started by adding your first company'}
              </p>
              <Button asChild>
                <Link to="/companies/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Company
                </Link>
              </Button>
            </div>
          ) : (
            <DataTable
              data={companies}
              columns={columns}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              total={totalCompanies}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              emptyMessage="No companies found"
            />
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        entityName={companyToDelete?.companyName}
        loading={deleting}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default CompanyList;
